/**
 * Referral Directory Page Component
 *
 * Provides React implementation of the referral directory with full feature parity
 * to the existing AngularJS version. Clean table-based interface with filtering.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Modal, Button, message } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { Table, FilterControls } from '../../components/shared/ui';
import type { TableColumn, FilterOption } from '../../components/shared/ui';
import { useReferralApi } from './hooks/useReferralApi';
import type { Referral, ReferralListData, ReferralTag } from './hooks/useReferralApi';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { setSelectedFacility } from '../../store/facilitiesSlice';
import ReferralDetailModal from './components/ReferralDetailModal';
import '../../styles/common/index.css';
import './ReferralPage.css';

const ReferralPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [filteredReferrals, setFilteredReferrals] = useState<Referral[]>([]);
  const [listData, setListData] = useState<ReferralListData | null>(null);
  const [loading, setLoading] = useState(true);

  // Filter states
  const [selectedSpecialty, setSelectedSpecialty] = useState<string>('');
  const [searchName, setSearchName] = useState('');

  // Modal states
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState<Referral | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);

  const { fetchReferralList, deleteReferral, isLoading } = useReferralApi();

  const normalizeTagId = (value: number | string | null | undefined): number | null => {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const normalized = Number(value);
    return Number.isFinite(normalized) ? normalized : null;
  };

  // Redux state for facilities (shared across app)
  const { facilities, selectedFacilityId } = useAppSelector(state => state.facilities);
  const location = useLocation();

  /**
   * Load referral list on mount
   */
  useEffect(() => {
    loadReferrals();
  }, []);

  /**
   * Apply filters when filter values or referral list changes
   */
  useEffect(() => {
    applyFilters();
  }, [selectedSpecialty, searchName, selectedFacilityId, referrals]);

  /**
   * Load referral list from API
   */
  const loadReferrals = async () => {
    try {
      setLoading(true);
      const data = await fetchReferralList();

      // Add computed color to each referral
      const referralsWithColor = data.referrals.map(ref => ({
        ...ref,
        referral_color: getReferralTagColor(ref, data.referral_tags)
      }));

      setReferrals(referralsWithColor);
      setListData(data);
    } catch (error) {
      message.error('Failed to load referral directory');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get referral tag color
   */
  const getReferralTagColor = (referral: Referral, tags: ReferralTag[]): string => {
    const normalizedTagId = normalizeTagId(referral.referral_tag_id);
    if (normalizedTagId === null) {
      return '#FFFFFF';
    }
    const tag = tags.find(t => t.id === normalizedTagId);
    return tag?.color || '#FFFFFF';
  };

  /**
   * Apply all active filters
   */
  const applyFilters = () => {
    let filtered = [...referrals];

    // Filter by facility
    if (selectedFacilityId !== null && listData) {
      const selectedFacility = listData.facilities.find(f => f.id === selectedFacilityId);
      if (selectedFacility) {
        filtered = filtered.filter(ref => {
          if (!ref.facilities || ref.facilities.length === 0) return false;

          // Handle both string array and object array
          if (typeof ref.facilities[0] === 'string') {
            return (ref.facilities as string[]).some(
              fac => fac.toLowerCase() === selectedFacility.name.toLowerCase()
            );
          } else {
            return (ref.facilities as any[]).some(
              fac => fac.name?.toLowerCase() === selectedFacility.name.toLowerCase()
            );
          }
        });
      }
    }

    // Filter by specialty
    if (selectedSpecialty) {
      filtered = filtered.filter(ref =>
        ref.specialty?.toLowerCase() === selectedSpecialty.toLowerCase()
      );
    }

    // Filter by name
    if (searchName.trim()) {
      const name = searchName.toLowerCase();
      filtered = filtered.filter(ref =>
        ref.name?.toLowerCase().includes(name)
      );
    }

    setFilteredReferrals(filtered);
  };

  /**
   * Handle referral row click
   */
  const handleReferralClick = (referral: Referral) => {
    setSelectedReferral(referral);
    setIsCreatingNew(false);
    setIsDetailModalOpen(true);
  };

  /**
   * Handle new referral button click
   */
  const handleNewReferral = () => {
    setSelectedReferral(null);
    setIsCreatingNew(true);
    setIsDetailModalOpen(true);
  };

  /**
   * Handle delete referral with confirmation
   */
  const handleDeleteReferral = async (referralId: number, event: React.MouseEvent) => {
    Modal.confirm({
      title: 'Delete Referral',
      content: 'Are you sure you want to delete this referral?',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      zIndex: 10000,
      onOk: async () => {
        try {
          const success = await deleteReferral(referralId);
          if (success) {
            message.success('Referral deleted successfully');
            // Remove from local state
            setReferrals(prev => prev.filter(r => r.id !== referralId));
          } else {
            message.error('Failed to delete referral');
          }
        } catch (error) {
          console.error('Delete error:', error);
          message.error('An error occurred while deleting');
        }
      }
    });
  };

  /**
   * Handle modal close
   */
  const handleModalClose = () => {
    setIsDetailModalOpen(false);
    setSelectedReferral(null);
    setIsCreatingNew(false);
  };

  /**
   * Handle referral save success
   */
  const handleReferralSaved = async (savedReferral: Referral) => {
    // Reload the full referral list to get properly formatted data
    await loadReferrals();
    handleModalClose();
  };

  /**
   * Get facility filter options
   */
  const getFacilityFilterOptions = (): FilterOption[] => {
    if (!listData) return [];

    return [
      { value: 'null', label: 'Any' },
      ...listData.facilities.map(f => ({
        value: String(f.id),
        label: f.name
      }))
    ];
  };

  /**
   * Table columns configuration
   */
  const columns: TableColumn[] = [
    {
      key: 'name',
      title: 'Name',
      width: '20%',
      onClick: handleReferralClick
    },
    {
      key: 'specialty',
      title: 'Specialty',
      width: '20%',
      onClick: handleReferralClick
    },
    {
      key: 'contact_email',
      title: 'Email',
      width: '20%',
      render: (value: string) => value ? (
        <a href={`mailto:${value}`} onClick={(e) => e.stopPropagation()}>
          {value}
        </a>
      ) : ''
    },
    {
      key: 'contact_phone',
      title: 'Phone',
      width: '15%',
      render: (value: string) => value ? (
        <a href={`tel:${value}`} onClick={(e) => e.stopPropagation()}>
          {value}
        </a>
      ) : ''
    },
    {
      key: 'facilities',
      title: 'Locations',
      width: '20%',
      render: (value: string[] | any[]) => {
        if (!value || value.length === 0) return '';
        if (typeof value[0] === 'string') {
          return value.join(', ');
        }
        return value.map((f: any) => f.name).join(', ');
      }
    },
    {
      key: 'actions',
      title: '',
      width: '5%',
      align: 'center',
      render: (_value: any, record: Referral) => (
        <DeleteOutlined
          style={{ color: '#ff4d4f', cursor: 'pointer', fontSize: '16px' }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleDeleteReferral(record.id, e);
          }}
          title="Delete Referral"
        />
      )
    }
  ];

  return (
    <div className="referral-page">
      {/* Directory Navigation with Tab Styling */}
      <div className="tec-tabs">
        <ul className="tec-tabs-nav">
          <li className="tec-tabs-nav-item">
            <Link
              to="/directory"
              className={`tec-tabs-nav-link ${location.pathname === '/directory' ? 'active' : ''}`}
            >
              EMPLOYEES
            </Link>
          </li>
          <li className="tec-tabs-nav-item">
            <Link
              to="/directory/referrals"
              className={`tec-tabs-nav-link ${location.pathname === '/directory/referrals' ? 'active' : ''}`}
            >
              REFERRALS
            </Link>
          </li>
        </ul>
      </div>

      {/* Filter Controls - matching DirectoryPage layout */}
      <div className="tec-container">
        <div className="tec-filter-controls">
          <div className="tec-filter-group">
            <label htmlFor="specialty-filter">Specialty:</label>
            <input
              id="specialty-filter"
              type="text"
              className="tec-form-control"
              placeholder="Search by specialty..."
              value={selectedSpecialty}
              onChange={(e) => setSelectedSpecialty(e.target.value)}
            />
          </div>

          <div className="tec-filter-group">
            <label htmlFor="name-filter">Name:</label>
            <input
              id="name-filter"
              type="text"
              className="tec-form-control"
              placeholder="Search by name..."
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
            />
          </div>

          <div className="tec-filter-group">
            <label htmlFor="facility-select">Facility:</label>
            <select
              id="facility-select"
              className="tec-form-control tec-form-select"
              value={selectedFacilityId ?? ''}
              onChange={(e) => dispatch(setSelectedFacility(e.target.value ? Number(e.target.value) : null))}
            >
              <option value="">All Facilities</option>
              {listData?.facilities.map(f => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </div>

          <div className="tec-filter-group">
            <Button
              type="primary"
              danger
              icon={<PlusOutlined />}
              onClick={handleNewReferral}
            >
              New Referral
            </Button>
          </div>
        </div>
      </div>

      {/* Referral Table */}
      <Table
        columns={columns}
        data={filteredReferrals}
        loading={loading || isLoading}
        emptyText="No referrals found"
        striped
        bordered
        hover
        onRowClick={handleReferralClick}
      />

      {/* Referral Detail Modal */}
      {listData && (
        <ReferralDetailModal
          visible={isDetailModalOpen}
          referral={selectedReferral}
          isCreating={isCreatingNew}
          listData={listData}
          onClose={handleModalClose}
          onSave={handleReferralSaved}
        />
      )}
    </div>
  );
};

export default ReferralPage;
